import React, { useRef, useState } from "react";

const HUBSPOT_PORTAL_ID = "25310090";
const HUBSPOT_FORM_ID = "16a0920c-4a7b-4820-aaa9-832a8287748b";

const ACCEPTED_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_BYTES = 8 * 1024 * 1024;

/** Reads a File into the bare base64 payload (no `data:...;base64,` prefix). */
const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || "");
            resolve(result.slice(result.indexOf(",") + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

type Props = {
    /** Job being applied for; submitted as HubSpot's hidden `jobtitle` field. */
    jobTitle: string;
};

const CareerApplyForm: React.FC<Props> = ({ jobTitle }) => {
    const [fullname, setFullname] = useState("");
    const [fullnameErr, setFullnameErr] = useState(false);
    const [email, setEmail] = useState("");
    const [emailErr, setEmailErr] = useState(false);
    const [phone, setPhone] = useState("");
    const [resume, setResume] = useState<File | null>(null);
    const [resumeErr, setResumeErr] = useState("");
    const [dragging, setDragging] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const acceptFile = (file: File | undefined) => {
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setResume(null);
            setResumeErr("Please upload a PDF or DOCX file.");
            return;
        }
        if (file.size > MAX_FILE_BYTES) {
            setResume(null);
            setResumeErr("Please keep the file under 8MB.");
            return;
        }
        setResume(file);
        setResumeErr("");
    };

    const validateInputs = () => {
        const emailRegEx =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        const isInvalidName = fullname.trim().length < 3;
        const isInvalidEmail =
            email.length < 2 || !emailRegEx.test(String(email).toLowerCase());

        setFullnameErr(isInvalidName);
        setEmailErr(isInvalidEmail);

        return !isInvalidName && !isInvalidEmail;
    };

    /**
     * Sends the resume through our own API route, which forwards it to HubSpot's
     * Files API. It cannot go straight from here: that call needs a private-app
     * token and api.hubapi.com does not allow cross-origin browser requests.
     */
    const uploadResume = async (file: File): Promise<string> => {
        const response = await fetch("/api/careers/upload", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                filename: file.name,
                contentType: file.type,
                data: await fileToBase64(file),
            }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload?.error || "The file could not be uploaded.");
        }
        return payload.url as string;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError("");
        if (!validateInputs() || submitting) return;

        setSubmitting(true);
        try {
            let resumeUrl = "";
            if (resume) {
                resumeUrl = await uploadResume(resume);
            }

            const response = await fetch(
                `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        submittedAt: Date.now(),
                        fields: [
                            { name: "jobtitle", value: jobTitle },
                            { name: "firstname", value: fullname },
                            { name: "email", value: email },
                            { name: "phone", value: phone },
                            { name: "select_resume", value: resumeUrl },
                            { name: "source_type", value: "Career Page" },
                        ],
                        context: { pageName: `Career — ${jobTitle}` },
                    }),
                }
            );

            if (!response.ok) {
                throw new Error("Your application could not be sent. Please try again.");
            }
            setSubmitted(true);
        } catch (error: any) {
            setSubmitError(error?.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className={"career-apply-form"}>
                <div className={"form career-apply-success"}>
                    <h4>Application received</h4>
                    <p>
                        Thanks for applying for {jobTitle}. Our team will review your
                        application and get back to you.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={"career-apply-form"}>
            <div className={"form"}>
                <form onSubmit={handleSubmit} noValidate>
                    <div className={"d-flex"}>
                        <div className={"input-wrapper"}>
                            <label>
                                Full name <span className={"required"}>*</span>
                            </label>
                            <input
                                type={"text"}
                                value={fullname}
                                placeholder={"Full name"}
                                className={`contact-input ${fullnameErr ? "is-error" : ""}`}
                                onChange={(e) => {
                                    setFullname(e.target.value);
                                    if (e.target.value.length > 0) setFullnameErr(false);
                                }}
                            />
                            <h6 className={`form-input-validation ${fullnameErr ? "error" : ""}`}>
                                Full name is required
                            </h6>
                        </div>
                        <div className={"input-wrapper"}>
                            <label>
                                Email <span className={"required"}>*</span>
                            </label>
                            <input
                                type={"email"}
                                value={email}
                                placeholder={"Email"}
                                className={`contact-input ${emailErr ? "is-error" : ""}`}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (e.target.value.length > 0) setEmailErr(false);
                                }}
                            />
                            <h6 className={`form-input-validation ${emailErr ? "error" : ""}`}>
                                {email.length < 1 ? "Email is required" : "Invalid email"}
                            </h6>
                        </div>
                    </div>

                    <div className={"input-wrapper"}>
                        <label>Phone Number</label>
                        <input
                            type={"tel"}
                            value={phone}
                            placeholder={"Your phone number"}
                            className={"contact-input"}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </div>

                    <div className={"input-wrapper"}>
                        <label>
                            Job Title <span className={"required"}>*</span>
                        </label>
                        {/* Fixed by the page being viewed; shown so the candidate can
                            see what they are applying for, but not editable. */}
                        <input
                            type={"text"}
                            value={jobTitle}
                            className={"contact-input"}
                            readOnly
                        />
                    </div>

                    <div className={"input-wrapper"}>
                        <label>Resume/CV</label>
                        <div
                            className={`resume-dropzone ${dragging ? "is-dragging" : ""} ${
                                resumeErr ? "is-error" : ""
                            }`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                acceptFile(e.dataTransfer.files?.[0]);
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type={"file"}
                                accept={".pdf,.doc,.docx"}
                                hidden
                                onChange={(e) => acceptFile(e.target.files?.[0])}
                            />
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M12 16V8m0 0-3 3m3-3 3 3" />
                                <path d="M20 16.5A3.5 3.5 0 0 0 17.5 10h-1A6 6 0 1 0 6 15.5" />
                            </svg>
                            <strong>
                                {resume ? resume.name : "Drop your files here or click to upload"}
                            </strong>
                            <span>Upload your PDF or DOCX files here</span>
                            <button
                                type={"button"}
                                className={"upload-file-btn"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                            >
                                {resume ? "Replace File" : "Upload File"}
                            </button>
                        </div>
                        <h6 className={`form-input-validation ${resumeErr ? "error" : ""}`}>
                            {resumeErr}
                        </h6>
                    </div>

                    <button type={"submit"} className={"primary"} disabled={submitting}>
                        {submitting ? "Submitting…" : "Submit Application"}
                    </button>
                    {submitError && (
                        <h6 className={"form-input-validation error"}>{submitError}</h6>
                    )}
                </form>
            </div>
        </div>
    );
};

export default CareerApplyForm;
